import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, ChevronRight, FileText } from "lucide-react";
import { LEGAL_DOCS, LEGAL_GROUPS, CONTACT } from "../data/legalContent";
import logoMark from "../assets/logo-mark.png";

/** Renders the long-form policy body: numbered/short heading lines become headings. */
function DocBody({ text }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-3 leading-relaxed text-[#C7D6EA]">
      {lines.map((raw, i) => {
        const line = raw.trimEnd();
        const key = `${i}-${line.slice(0, 24)}`;
        if (!line.trim()) return <div key={key} className="h-1" />;
        const isNumbered = /^\d+\.\s+\S/.test(line) && line.length < 90;
        const isShortHeading = !line.startsWith("•") && !line.startsWith("☐") && line.length < 70 && !line.endsWith(".") && !line.endsWith(";") && !line.endsWith(":") && !line.includes("@");
        if (isNumbered || isShortHeading) {
          return <h2 key={key} className="font-display text-lg text-white pt-3 tracking-tight">{line}</h2>;
        }
        if (line.startsWith("•") || line.startsWith("☐")) {
          return <p key={key} className="pl-4 text-sm text-[#9AAEC6]">{line}</p>;
        }
        return <p key={key} className="text-sm">{line}</p>;
      })}
    </div>
  );
}

export default function TrustCenter() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const doc = slug ? LEGAL_DOCS[slug] : null;

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Unknown slug → back to index
  useEffect(() => { if (slug && !doc) navigate("/trust-center", { replace: true }); }, [slug, doc, navigate]);

  return (
    <div className="min-h-screen bps-bg text-white" data-testid="trust-center">
      <header className="border-b border-white/[0.06] px-5 sm:px-8 py-4 sticky top-0 z-30 bg-[#040814]/85 backdrop-blur-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="trust-home-link">
            <img src={logoMark} alt="Freedom From Back Pain" className="w-9 h-9 rounded-xl object-cover" />
            <span className="font-display text-sm tracking-tight">Freedom From Back Pain</span>
          </Link>
          {slug ? (
            <Link to="/trust-center" className="text-sm text-[#9AAEC6] hover:text-white inline-flex items-center gap-1.5" data-testid="trust-back-link">
              <ArrowLeft size={15} /> Trust Center
            </Link>
          ) : (
            <Link to="/" className="text-sm text-[#9AAEC6] hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft size={15} /> Home
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 pb-20">
        {!doc ? (
          <>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#22D3EE]/30 bg-[#22D3EE]/10 px-4 py-1.5 text-[#22D3EE] text-xs font-mono uppercase tracking-[0.18em]">
                <ShieldCheck size={14} /> Trust Center
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-4">Freedom From Back Pain™ Trust Center</h1>
              <p className="text-[#9AAEC6] mt-3 max-w-2xl">Everything that governs your experience with the Program — privacy, terms, safety, and how to reach us. Transparent and in one place.</p>
            </motion.div>

            <div className="mt-10 space-y-8">
              {LEGAL_GROUPS.map((group) => (
                <div key={group.label}>
                  <div className="bps-section-head text-[#22D3EE] mb-3" style={{ textShadow: "0 0 14px rgba(34,211,238,0.45)" }}>{group.label}</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {group.slugs.map((s) => {
                      const d = LEGAL_DOCS[s];
                      if (!d) return null;
                      return (
                        <Link
                          key={s}
                          to={`/trust-center/${s}`}
                          data-testid={`trust-link-${s}`}
                          className="group flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-[#22D3EE]/50 hover:bg-white/[0.05] transition-all"
                        >
                          <div className="w-9 h-9 rounded-xl bg-[#22D3EE]/12 border border-[#22D3EE]/30 flex items-center justify-center shrink-0">
                            <FileText size={16} className="text-[#22D3EE]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display text-white leading-snug">{d.title}</div>
                            <div className="text-xs text-[#9AAEC6] mt-0.5 leading-relaxed">{d.summary}</div>
                          </div>
                          <ChevronRight size={16} className="text-[#7C8FA8] shrink-0 group-hover:text-[#22D3EE] transition-colors" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-[#9AAEC6]">
              <div className="font-display text-white">{CONTACT.company}</div>
              <div className="mt-1">Email: <a href={`mailto:${CONTACT.email}`} className="text-[#22D3EE] hover:underline">{CONTACT.email}</a></div>
              <div>Website: {CONTACT.website}</div>
              <p className="mt-4 text-xs text-[#4F627B] leading-relaxed">
                © {new Date().getFullYear()} Perfectore Corp. All Rights Reserved. Freedom From Back Pain™, Back Pain Transformer™, Freedom Promise™, and Perfectore™ are intellectual property of Perfectore Corp. The information provided is for educational and informational purposes only and is not medical advice.
              </p>
            </div>
          </>
        ) : (
          <motion.article initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid={`trust-doc-${slug}`}>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{doc.title}</h1>
            {doc.summary && <p className="text-[#9AAEC6] mt-2">{doc.summary}</p>}
            <div className="h-px bg-white/10 my-6" />
            <DocBody text={doc.body} />
            <div className="mt-10">
              <Link to="/trust-center" className="text-sm text-[#22D3EE] hover:underline inline-flex items-center gap-1.5">
                <ArrowLeft size={15} /> Back to Trust Center
              </Link>
            </div>
          </motion.article>
        )}
      </main>
    </div>
  );
}
